import { ChangeEvent, MouseEvent, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DataTable, DataTableCellClassNameOptions, DataTablePageParams, DataTableRowClickEventParams } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { useCreateOrder } from "../../../api/queries/useCreateOrder"
import { CreateOrderResponseData, GetOrdersRequestQuery, Order, OrderBulkFinishPayload, ORDER_STATE, GetOrdersFilters } from "../../../api/types/order"
import { useOrders } from "../../../api/queries/useOrders"
import { createUrlParams } from "../../../api/client"
import { formatDate, formatPayloadDate, parseDate } from "../../../common/datetime"
import { storage } from "../../../common/storage"
import { OrderStateTag } from "../StateTag"
import { InputText } from "primereact/inputtext"
import { useDebounce } from "../../../common/useDebounce"
import { MultiSelect, MultiSelectChangeParams } from "primereact/multiselect"
import { Calendar, CalendarChangeParams } from "primereact/calendar"
import { ConstantsResponseData, OrderState } from "../../../api/types/constants"
import { useConstants } from "../../../api/queries/useConstants"
import TermsConfirm from "../../TermsConfirm"
import userContext from "../../../context/user"
import { useOrderBulkFinish } from "../../../api/queries/useOrderBulkFinish"
import { Checkbox } from "primereact/checkbox"
import { OrderDocumentsModal } from "../DocumentsModal"
import { Toast } from "primereact/toast"
import { useUsers } from "../../../api/queries/useUsers"
import { User } from "../../../api/types/user"
import styles from "./styles.module.css"
import { OverlayPanel } from "primereact/overlaypanel"
import OrderTemplateListOverlay from "./template-list"
import { useOrderDocuments } from "../../../api/queries/useOrderDocuments"

export default function OrderList() {
  const { user } = useContext(userContext)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createOrder = useCreateOrder()
  const orderBulkFinish = useOrderBulkFinish()
  const { data: constants }: { data: ConstantsResponseData | undefined } = useConstants()
  const { data: users } = useUsers({ limit: 0, offset: 0, isUserManagement: false })

  const [orderDocsOrders, setOrderDocsOrders] = useState<string[] | null>(null)
  const onOrderDocumentsModalHide = () => setOrderDocsOrders(null)

  const offsetParam: string = searchParams.get("offset") || ""
  const limitParam: string = searchParams.get("limit") || ""
  const [offset, setOffset] = useState<number>(parseInt(offsetParam) || 0)
  const [limit, setLimit] = useState<number>(parseInt(limitParam) || storage.get("limit") || 20)
  const [isListPrintActive, setIsListPrintActive] = useState<boolean>(false)

  const onPage = ({ first, rows }: DataTablePageParams): void => {
    setOffset(first)
    setLimit(rows)
    storage.set("limit", rows)
  }

  const onNewOrderClick = async () => {
    let data: CreateOrderResponseData = await createOrder.mutateAsync()
    navigate(`/orders/${data.results.orderCreate.uuid}/loading`)
  }

  const onImportClick = () => {
    navigate(`/orders/import/csv`)
  }

  const onRowClick = (row: DataTableRowClickEventParams) => {
    const target: HTMLElement = row.originalEvent.target as HTMLElement
    const order: Order = row.data

    if (
      target.classList.contains("selectable") ||
      target.classList.contains("p-checkbox-icon") ||
      isListPrintActive
    ) {
      return toggleOrderSelection(order)
    }

    let step = ""

    if (order.state === ORDER_STATE.IN_PROGRESS) {
      step = "loading"
    } else {
      step = "summary"
    }

    navigate(`/orders/${order.uuid}/${step}`)
  }

  const rowClassName = (order: Order) => ({
    "cursor-pointer": true,
  })

  const cellClassName = (_: any, options: DataTableCellClassNameOptions) => ({
    "text-center": options.column.props.columnKey === "order-selection",
    "selectable": options.column.props.columnKey === "order-selection" &&
      (options.rowData as Order).state === ORDER_STATE.TO_SENT,
  })

  const EMPTY = <span>&mdash;</span>
  const DELAY = 500

  const storageFilters: GetOrdersFilters | null = storage.get("order-list-filters")

  // code
  const [code, setCode] = useState<string>(searchParams.get("fCode") || storageFilters?.fCode || "")
  const onCodeChange = (event: ChangeEvent<HTMLInputElement>): void => setCode(event.target.value)
  const tCode = (row: Order): string => row.code || ""
  const fCode = useDebounce(code, DELAY)
  const fCodeEl = (): ReactNode => <InputText value={code} onChange={onCodeChange}
    placeholder={"Kód"} />

  // reference
  const [reference, setReference] = useState<string>(searchParams.get("fReference") || storageFilters?.fReference || "")
  const onReferenceChange = (event: ChangeEvent<HTMLInputElement>): void => setReference(event.target.value)
  const tReference = (row: Order): ReactNode => row.calculation
    ? row.calculation.reference || EMPTY
    : EMPTY
  const fReference = useDebounce(reference, DELAY)
  const fReferenceEl = (): ReactNode => <InputText value={reference} onChange={onReferenceChange}
    placeholder={"Reference"} />

  // created at
  const createdAtFromParam = parseDate(searchParams.get("createdAtFrom") || storageFilters?.createdAtFrom)
  const createdAtToParam = parseDate(searchParams.get("createdAtTo") || storageFilters?.createdAtTo)
  const [createdAtFrom, setCreatedAtFrom] = useState<Date | Date[] | undefined>(createdAtFromParam)
  const [createdAtTo, setCreatedAtTo] = useState<Date | Date[] | undefined>(createdAtToParam)
  const onCratedAtFromChange = (event: CalendarChangeParams): void => setCreatedAtFrom(event.value)
  const onCratedAtToChange = (event: CalendarChangeParams): void => setCreatedAtTo(event.value)
  const tCreatedAt = (row: Order): string => formatDate(row.createdAt) || row.createdAt
  const fCreatedAtEl = (): ReactNode => (
    <>
      <Calendar value={createdAtFrom} onChange={onCratedAtFromChange} dateFormat={"d. m. yy"}
        showButtonBar={true} className={"w-6 inline-flex border-right-1 border-black-alpha-10"}
        placeholder={"Od"} />
      <Calendar value={createdAtTo} onChange={onCratedAtToChange} dateFormat={"d. m. yy"}
        showButtonBar={true} className={"w-6 inline-flex"}
        placeholder={"Do"} />
    </>
  )

  // created by
  const [createdBy, setCreatedBy] = useState<string[]>(
    (searchParams.get("fCreatedBy") || storageFilters?.fCreatedBy || "")
      .split(",")
      .filter((s) => s !== "")
  )
  const onCreatedByChange = (event: MultiSelectChangeParams) => setCreatedBy(event.value)
  const tCreatedBy = (row: Order): ReactNode => row.createdBy ? `${row.createdBy.lastname} ${row.createdBy.firstname}` : ""
  const fCreatedByItemTemplate = (row: User) => `${row.lastname} ${row.firstname}`
  const fCreatedBySelectedItemTemplate = (uuid: string) => {
    const user = users?.results.userList.find((user) => user.uuid === uuid)
    if (user) {
      return `${user.lastname} ${user.firstname}`
    }
    return ""
  }
  const fCreatedByEl = (): ReactNode => <MultiSelect value={createdBy} onChange={onCreatedByChange}
    options={users?.results.userList || []} placeholder={"Vytvořil"} itemTemplate={fCreatedByItemTemplate}
    optionValue={"uuid"} selectedItemTemplate={fCreatedBySelectedItemTemplate}
    maxSelectedLabels={1} selectedItemsLabel={createdBy.length < 5 ? "{0} uživatelé" : "{0} uživatelů"}
  />

  // loading date
  const loadingDateFromParam = parseDate(searchParams.get("loadingDateFrom") || storageFilters?.loadingDateFrom)
  const loadingDateToParam = parseDate(searchParams.get("loadingDateTo") || storageFilters?.loadingDateTo)
  const [loadingDateFrom, setLoadingDateFrom] = useState<Date | Date[] | undefined>(loadingDateFromParam)
  const [loadingDateTo, setLoadingDateTo] = useState<Date | Date[] | undefined>(loadingDateToParam)
  const onLoadingDateFromChange = (event: CalendarChangeParams): void => setLoadingDateFrom(event.value)
  const onLoadingDateToChange = (event: CalendarChangeParams): void => setLoadingDateTo(event.value)
  const tLoadingDate = (row: Order): ReactNode => row.calculation
    ? formatDate(row.calculation.loadingDate || "")
    : EMPTY
  const fLoadingDateEl = (): ReactNode => (
    <>
      <Calendar value={loadingDateFrom} onChange={onLoadingDateFromChange} dateFormat={"d. m. yy"}
        showButtonBar={true} className={"w-6 inline-flex border-right-1 border-black-alpha-10"}
        placeholder={"Od"} />
      <Calendar value={loadingDateTo} onChange={onLoadingDateToChange} dateFormat={"d. m. yy"}
        showButtonBar={true} className={"w-6 inline-flex"}
        placeholder={"Do"} />
    </>
  )

  // loading name
  const [loading, setLoading] = useState<string>(searchParams.get("fLoading") || storageFilters?.fLoading || "")
  const onLoadingChange = (event: ChangeEvent<HTMLInputElement>): void => setLoading(event.target.value)
  const tLoading = (row: Order): ReactNode => row.loading
    ? row.loading.name
    : EMPTY
  const fLoading = useDebounce(loading, DELAY)
  const fLoadingEl = (): ReactNode => <InputText value={loading} onChange={onLoadingChange}
    placeholder={"Odesílatel"} />

  // unloading name
  const [unloading, setUnloading] = useState<string>(searchParams.get("fUnloading") || storageFilters?.fUnloading || "")
  const onUnloadingChange = (event: ChangeEvent<HTMLInputElement>): void => setUnloading(event.target.value)
  const tUnloading = (row: Order): ReactNode => row.unloading
    ? row.unloading.name
    : EMPTY
  const fUnloading = useDebounce(unloading, DELAY)
  const fUnloadingEl = (): ReactNode => <InputText value={unloading} onChange={onUnloadingChange}
    placeholder={"Příjemce"} />

  // tansportation
  const tTransportation = (row: Order): ReactNode => row.loading && row.unloading
    ? row.loading.country === "CZ" && row.unloading.country === "CZ"
      ? "Tuzemská"
      : "Zahraniční"
    : EMPTY

  // state
  const stateParam = (searchParams.get("state") || storageFilters?.state || "")
    .split(",")
    .filter((s) => s !== "")
    .map((s) => parseInt(s))
  const stateOptions: OrderState[] = constants?.results.orderStates || []
  const [state, setState] = useState<number[]>(stateParam || [])
  const onStateChange = (event: MultiSelectChangeParams) => setState(event.value)
  const tState = (row: Order): ReactNode => (
    <OrderStateTag value={row.state} options={constants?.results.orderStates || []} />
  )
  const fStateEl = (): ReactNode => <MultiSelect value={state} onChange={onStateChange}
    options={stateOptions} placeholder={"Stav"}
    maxSelectedLabels={1} selectedItemsLabel={state.length < 5 ? "{0} stavy" : "{0} stavů"}
    disabled={isListPrintActive} />

  useEffect(() => {
    if (isListPrintActive) {
      return
    }

    let filters: GetOrdersFilters = {}

    if (fCode) {
      filters.fCode = fCode
    }

    if (fReference) {
      filters.fReference = fReference
    }

    if (fLoading) {
      filters.fLoading = fLoading
    }

    if (fUnloading) {
      filters.fUnloading = fUnloading
    }

    if (createdBy.length > 0) {
      filters.fCreatedBy = createdBy.join(",")
    }

    if (state.length > 0) {
      filters.state = state.join(",")
    }

    if (createdAtFrom) {
      filters.createdAtFrom = formatPayloadDate(createdAtFrom as Date)
    }

    if (createdAtTo) {
      filters.createdAtTo = formatPayloadDate(createdAtTo as Date)
    }

    if (loadingDateFrom) {
      filters.loadingDateFrom = formatPayloadDate(loadingDateFrom as Date)
    }

    if (loadingDateTo) {
      filters.loadingDateTo = formatPayloadDate(loadingDateTo as Date)
    }

    storage.set("order-list-filters", filters)
  }, [fCode, fReference, fLoading, fUnloading, createdBy, state, createdAtFrom, createdAtTo,
    loadingDateFrom, loadingDateTo, isListPrintActive])

  const tPieces = (row: Order) => row.goods
    ? `${row.goods.reduce((sum, good) => sum + good.pieces, 0)} ks`
    : EMPTY

  const tWeight = (row: Order) => row.goods
    ? `${row.goods.reduce((sum, good) => sum + good.weight, 0)} kg`
    : EMPTY

  let payload: GetOrdersRequestQuery = useMemo((): GetOrdersRequestQuery => {
    let obj: GetOrdersRequestQuery = {
      limit,
      offset,
    }

    if (fCode) {
      obj.fCode = fCode
    }

    if (fReference) {
      obj.fReference = fReference
    }

    if (fLoading) {
      obj.fLoading = fLoading
    }

    if (fUnloading) {
      obj.fUnloading = fUnloading
    }

    if (createdBy.length > 0) {
      obj.fCreatedBy = createdBy.join(",")
    }

    if (state.length > 0) {
      obj.state = state.join(",")
    }

    if (createdAtFrom) {
      obj.createdAtFrom = formatPayloadDate(createdAtFrom as Date)
    }

    if (createdAtTo) {
      obj.createdAtTo = formatPayloadDate(createdAtTo as Date)
    }

    if (loadingDateFrom) {
      obj.loadingDateFrom = formatPayloadDate(loadingDateFrom as Date)
    }

    if (loadingDateTo) {
      obj.loadingDateTo = formatPayloadDate(loadingDateTo as Date)
    }

    return obj
  }, [limit, offset, fCode, fReference, fLoading, fUnloading, state, createdAtFrom, createdAtTo,
    loadingDateFrom, loadingDateTo, createdBy])

  const { data: orders, isLoading } = useOrders(payload)

  useEffect(() => {
    setSelectedOrders([]) // reset selection when orders changed

    const params = Object.assign({}, payload)

    if (isListPrintActive) {
      delete params.state
    }

    navigate({
      search: createUrlParams(params),
    }, {
      replace: true,
    })
  }, [payload, navigate, searchParams, isListPrintActive])

  // list print
  const onEnableListPrintClick = () => {
    setIsListPrintActive(true)
    setState([10, 11, 12])
  }

  const onDisableListPrintClick = () => {
    setIsListPrintActive(false)
    setSelectedOrders([])
    resetFilters()
  }

  const orderDocs = useOrderDocuments()

  const onListPrintFinishClick = async () => {
    setOrderDocsOrders(selectedOrders)
  }

  // order bulk send
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [showTermsConfirmDialog, setShowTermsConfirmDialog] = useState<boolean>(false)

  const selectableOrders: Order[] =
    isListPrintActive
      ? orders?.results.orderList || []
      : orders
        ? orders.results.orderList.filter((order: Order) => order.state === ORDER_STATE.TO_SENT)
        : []

  const tOrderSelection = (order: Order): ReactNode =>
    order.state === ORDER_STATE.TO_SENT || isListPrintActive
      ? <Checkbox checked={selectedOrders.indexOf(order.uuid) > -1}
        onChange={() => toggleOrderSelection(order)} />
      : ""

  const fOrderSelection = (): ReactNode => (
    <div className={"text-center"}>
      <Checkbox checked={selectedOrders.length > 0 && selectedOrders.length === selectableOrders.length}
        onChange={toggleAllOrderSelection} />
    </div>
  )

  const toggleOrderSelection = (order: Order): void => {
    if (selectedOrders.indexOf(order.uuid) > -1) {
      setSelectedOrders(selectedOrders.filter((uuid: string) => uuid !== order.uuid))
    } else {
      if (order.state === ORDER_STATE.TO_SENT || isListPrintActive) {
        setSelectedOrders([...selectedOrders, order.uuid])
      }
    }
  }

  const toggleAllOrderSelection = (): void => {
    if (selectableOrders.length === selectedOrders.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(selectableOrders.map((order: Order) => order.uuid))
    }
  }

  const termsConfirmCallback = (success: boolean) => {
    if (success) {
      sendOrders()
    }
    setShowTermsConfirmDialog(false)
  }

  const onBulkFinishClick = (): void => {
    if (user?.isTermsConfirmed) {
      sendOrders()
    } else {
      setShowTermsConfirmDialog(true)
    }
  }

  const sendOrders = async (): Promise<void> => {
    const payload: OrderBulkFinishPayload = {
      orders: selectedOrders,
    }

    await orderBulkFinish.mutateAsync(payload)
    savedMessageRef.current?.show({ severity: "success", summary: "Objednávky odeslány" })
    setSelectedOrders([])
    setOrderDocsOrders(selectedOrders)
  }

  const savedMessageRef = useRef<Toast>(null)

  const orderTemplateOverlayPanelRef = useRef<any>(null)
  const onOrderTemplateOverlayPanelShow = (event: MouseEvent<HTMLButtonElement>) =>
    orderTemplateOverlayPanelRef.current.toggle(event)

  const resetFilters = () => {
    const filters: GetOrdersFilters | null = storage.get("order-list-filters")

    setCode(filters?.fCode || "")
    setReference(filters?.fReference || "")
    setLoading(filters?.fLoading || "")
    setUnloading(filters?.fUnloading || "")
    setCreatedBy((filters?.fCreatedBy || "").split(",").filter((s) => s !== ""))
    setState((filters?.state || "").split(",").filter((s) => s !== "").map((s) => parseInt(s)))
    setCreatedAtFrom(parseDate(filters?.createdAtFrom))
    setCreatedAtTo(parseDate(filters?.createdAtTo))
    setLoadingDateFrom(parseDate(filters?.loadingDateFrom))
    setLoadingDateTo(parseDate(filters?.loadingDateTo))
  }

  const removeFilters = () => {
    setCode("")
    setReference("")
    setLoading("")
    setUnloading("")
    setCreatedBy([])
    if (!isListPrintActive) {
      setState([])
    }
    setCreatedAtFrom(undefined)
    setCreatedAtTo(undefined)
    setLoadingDateFrom(undefined)
    setLoadingDateTo(undefined)

    setTimeout(() => {
      storage.delete("order-list-filters")
    }, 1000)
  }

  return (
    <>
      <TermsConfirm show={showTermsConfirmDialog} callback={termsConfirmCallback} />
      <OrderDocumentsModal orders={orderDocsOrders} onHide={onOrderDocumentsModalHide} />
      <Toast ref={savedMessageRef} />

      <div className={"col-12"}>
        <div className={"flex align-items-center"}>
          <Button label={"Nová objednávka"} title="Nová objednávka"
            icon={"pi pi-plus"} onClick={onNewOrderClick}
            disabled={createOrder.isLoading || isListPrintActive}
            className={"p-button p-button-primary p-button-sm p-button-responsive mr-3"} />

          <Button label={"Objednávka ze šablony"} title="Objednávka ze šablony"
            icon={"pi pi-plus"} onClick={onOrderTemplateOverlayPanelShow}
            disabled={createOrder.isLoading || isListPrintActive}
            className={"p-button p-button-primary p-button-sm p-button-responsive mr-3"}>
            <i className="pi pi-angle-down ml-2" />
          </Button>

          <Button label={"Import"} title={"Import"}
            icon={"pi pi-upload"} onClick={onImportClick}
            disabled={createOrder.isLoading || isListPrintActive}
            className={"p-button p-button-primary p-button-sm p-button-responsive mr-3"} />

          {isListPrintActive ? (
            <Button label="Tisk soupisky" title="Tisk soupisky"
              icon="pi pi-times" onClick={onDisableListPrintClick}
              disabled={orderDocs.isLoading} className={"p-button p-button-primary p-button-sm p-button-responsive mr-3"} />
          ) : (
            <Button label="Tisk soupisky" title="Tisk soupisky"
              icon="pi pi-print" onClick={onEnableListPrintClick}
              disabled={createOrder.isLoading} className={"p-button p-button-primary p-button-sm p-button-responsive mr-3"} />
          )}

          <OverlayPanel ref={orderTemplateOverlayPanelRef}>
            <OrderTemplateListOverlay />
          </OverlayPanel>

          <div className={"ml-auto"}>
            {isListPrintActive ? (
              <Button className={"p-button p-button-secondary p-button-sm p-button-responsive"}
                label={"Generovat soupisku"} title="Generovat soupisku"
                icon={`pi pi-${orderDocs.isLoading ? "spin pi-spinner" : "print"}`}
                onClick={onListPrintFinishClick}
                disabled={selectedOrders.length === 0 || orderDocs.isLoading} />
            ) : (
              <Button className={"p-button p-button-secondary p-button-sm p-button-responsive"}
                label={"Odeslat vybrané"} title="Odeslat vybrané"
                icon={`pi pi-${orderBulkFinish.isLoading ? "spin pi-spinner" : "send"}`}
                onClick={onBulkFinishClick}
                disabled={selectedOrders.length === 0 || (user && !user.isActive) || orderBulkFinish.isLoading || isListPrintActive} />
            )}
          </div>
        </div>
      </div>

      <div className={"col-12"}>
        <div className="flex align-items-start">
          <Button icon="pi pi-filter-slash"
            className="p-button p-button-secondary p-button-sm p-button-responsive border-noround-right p-button-reset-orders-filters"
            onClick={removeFilters}
            tooltip="Zrušit nastavené filtry"
            tooltipOptions={{
              position: "top",
            }}
          />

          <DataTable
            emptyMessage={"Žádné záznamy"} className={"p-datatable-sm p-datatable-filters p-datatable-orders"}
            paginator={true} lazy={true} rows={limit} first={offset}
            totalRecords={orders?.results.totalCount}
            loading={isLoading} onPage={onPage} value={orders?.results.orderList}
            onRowClick={onRowClick} rowClassName={rowClassName} rowHover={true} stripedRows={true}
            paginatorTemplate={"FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"}
            rowsPerPageOptions={[20, 50, 100]}
            filterDisplay={"row"} cellClassName={cellClassName}>

            <Column header={"Kód"} body={tCode}
              filter={true} filterElement={fCodeEl} showFilterMenu={false}
              className={styles.colCode} />

            <Column header={"Reference"} body={tReference}
              filter={true} filterElement={fReferenceEl} showFilterMenu={false} />

            <Column header={"Vytvořeno"} body={tCreatedAt}
              filter={true} filterElement={fCreatedAtEl} showFilterMenu={false}
              className={styles.colCreatedAt} />

            {(user?.isAdmin || user?.canManageUsers) && (
              <Column header={"Vytvořil"} body={tCreatedBy}
                filter={true} filterElement={fCreatedByEl} showFilterMenu={false} />
            )}

            <Column header={"Odběr"} body={tLoadingDate}
              filter={true} filterElement={fLoadingDateEl} showFilterMenu={false}
              className={styles.colLoadingDate} />

            <Column header={"Odesílatel"} body={tLoading}
              filter={true} filterElement={fLoadingEl} showFilterMenu={false} />

            <Column header={"Příjemce"} body={tUnloading}
              filter={true} filterElement={fUnloadingEl} showFilterMenu={false} />

            <Column header={"Kusy"} body={tPieces}
              className={styles.colPieces} />

            <Column header={"Hmotnost"} body={tWeight}
              className={styles.colWeight} />

            <Column header={"Přeprava"} body={tTransportation}
              className={styles.colTransportation} />

            <Column header={"Stav"} body={tState}
              filter={true} filterElement={fStateEl} showFilterMenu={false}
              className={styles.colState} />

            <Column columnKey={"order-selection"}
              header={isListPrintActive ? "Vybrat" : "Odeslat"} body={tOrderSelection}
              filter={true} filterElement={fOrderSelection} showFilterMenu={false}
              className={styles.colOrderSelection} />
          </DataTable>
        </div>
      </div>
    </>
  )
}
